import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronDown, ChevronUp, Check } from 'lucide-react';
import InputField from '../../ui/InputField';
import CustomDropdown from '../../ui/CustomDropdown';
import { selectRewardForm, updateRewardForm } from '../../../store/slices/gamificationSlice';

const RewardEventSection = () => {
  const dispatch = useDispatch();
  const rewardForm = useSelector(selectRewardForm);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const eventOptions = [
    { value: 'CROSS_SALES', label: 'Cross $X in sales' },
    { value: 'POST_FREQUENCY', label: 'Posts X times every Y period' },
    { value: 'IS_ONBOARDED', label: 'Is Onboarded' },
  ];

  const durationOptions = [
    { value: '14_DAYS', label: '14 days' },
    { value: '1_MONTH', label: '1 month' },
    { value: '2_MONTHS', label: '2 months' },
    { value: '3_MONTHS', label: '3 months' },
    { value: '1_YEAR', label: '1 year' },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEventTypeChange = (value) => {
    dispatch(updateRewardForm({ field: 'eventType', value }));
    // Reset event config when changing type
    dispatch(updateRewardForm({ field: 'eventConfig.salesAmount', value: '' }));
    dispatch(updateRewardForm({ field: 'eventConfig.postCount', value: '' }));
    dispatch(updateRewardForm({ field: 'eventConfig.duration', value: '' }));
  };

  const getEventDisplay = () => {
    if (!rewardForm.eventType) return 'Select an event';

    switch (rewardForm.eventType) {
      case 'CROSS_SALES':
        return rewardForm.eventConfig.salesAmount
          ? `Cross $${rewardForm.eventConfig.salesAmount} in sales`
          : 'Cross $X in sales';
      case 'POST_FREQUENCY':
        if (rewardForm.eventConfig.postCount && rewardForm.eventConfig.duration) {
          const durationLabel = durationOptions.find(d => d.value === rewardForm.eventConfig.duration)?.label;
          return `Posts ${rewardForm.eventConfig.postCount} times every ${durationLabel}`;
        }
        return 'Posts X times every Y period';
      case 'IS_ONBOARDED':
        return 'Is Onboarded';
      default:
        return 'Select an event';
    }
  };

  const isSaveDisabled = () => {
    if (!rewardForm.eventType) return true;

    switch (rewardForm.eventType) {
      case 'CROSS_SALES':
        return !rewardForm.eventConfig.salesAmount;
      case 'POST_FREQUENCY':
        return !rewardForm.eventConfig.postCount || !rewardForm.eventConfig.duration;
      case 'IS_ONBOARDED':
        return false;
      default:
        return true;
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-600">
        Reward event <span className="text-red-500">*</span>
      </label>

      <div className="relative w-full" ref={dropdownRef}>
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`
            w-full px-4 py-2.5 rounded-lg border-2 transition-all duration-200 text-sm
            flex items-center justify-between
            ${isOpen ? 'border-purple-500' : 'border-gray-300'}
            bg-white hover:border-purple-400
            focus:outline-none
          `}
        >
          <span className={rewardForm.eventType ? 'text-gray-900' : 'text-gray-400'}>
            {getEventDisplay()}
          </span>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-[9999] w-full mt-2 bg-white border-2 border-purple-500 rounded-lg shadow-lg overflow-hidden">
            {eventOptions.map((option) => {
              const isSelected = option.value === rewardForm.eventType;
              return (
                <div key={option.value}>
                  <button
                    type="button"
                    onClick={() => handleEventTypeChange(option.value)}
                    className={`
                      w-full px-4 py-2.5 text-left flex items-center justify-between text-sm
                      transition-colors duration-150
                      ${isSelected ? 'bg-purple-50 text-purple-600' : 'text-gray-700 hover:bg-gray-50'}
                    `}
                  >
                    <span className={isSelected ? 'font-medium' : ''}>{option.label}</span>
                    {isSelected && <Check className="w-4 h-4 text-purple-600" />}
                  </button>

                  {/* Inline Input for Cross Sales */}
                  {isSelected && option.value === 'CROSS_SALES' && (
                    <div className="px-4 pb-3 bg-purple-50">
                      <InputField
                        prefix="$"
                        placeholder="e.g. 100"
                        type="number"
                        value={rewardForm.eventConfig.salesAmount}
                        onChange={(value) =>
                          dispatch(updateRewardForm({ field: 'eventConfig.salesAmount', value }))
                        }
                      />
                    </div>
                  )}

                  {/* Inline Inputs for Post Frequency */}
                  {isSelected && option.value === 'POST_FREQUENCY' && (
                    <div className="px-4 pb-3 space-y-2 bg-purple-50">
                      <InputField
                        placeholder="e.g. 4"
                        type="number"
                        value={rewardForm.eventConfig.postCount}
                        onChange={(value) =>
                          dispatch(updateRewardForm({ field: 'eventConfig.postCount', value }))
                        }
                      />
                      <CustomDropdown
                        options={durationOptions}
                        value={rewardForm.eventConfig.duration}
                        onChange={(value) =>
                          dispatch(updateRewardForm({ field: 'eventConfig.duration', value }))
                        }
                        placeholder="Select duration"
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Save/Cancel Buttons at Bottom */}
            {rewardForm.eventType && (
              <div className="px-4 py-3 flex gap-3 border-t border-gray-200 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    dispatch(updateRewardForm({ field: 'eventConfig.salesAmount', value: '' }));
                    dispatch(updateRewardForm({ field: 'eventConfig.postCount', value: '' }));
                    dispatch(updateRewardForm({ field: 'eventConfig.duration', value: '' }));
                    dispatch(updateRewardForm({ field: 'eventType', value: null }));
                  }}
                  className="flex-1 px-4 py-2.5 bg-white border-2 border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  disabled={isSaveDisabled()}
                  className={`
                    flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors
                    ${isSaveDisabled()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-primary/90'
                    }
                  `}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardEventSection;
